
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import Region, { isRegionLocalId } from '@/store/models/region';
import Country from '@/store/models/country';
import { SettingsActions } from '@/store/modules/settings/types';

import FormInput from '@/components/controls/form-input.vue';
import SubmitButton from '@/components/controls/submit-button.vue';
import { AutocompleteOption } from '@/components/controls/autocomplete.vue';

export default Vue.extend({
  name: 'RegionSettingsForm',
  inject: {
    $validator: '$validator',
  },
  components: {
    FormInput,
    SubmitButton,
  },
  data(): {
    expandedRegion?: number | string;
    countryPickSearch: string;
    pickedCountryIds: Array<number | string>;
  } {
    return {
      expandedRegion: undefined,
      countryPickSearch: '',
      pickedCountryIds: [],
    };
  },
  computed: {
    ...mapState('settings', ['regions', 'editableRegions', 'editableCountries']),
    ...mapGetters('settings', {
      isLoading: 'editableSettingsIsLoading',
      isSaving: 'editableSettingsIsSaving',
    }),
    isFetching() {
      return this.isLoading || this.isSaving;
    },
  },
  methods: {
    ...mapActions('settings', {
      updateEditableList: SettingsActions.UPDATE_EDITABLE_REGIONS,
      updateEditable: SettingsActions.UPDATE_EDITABLE_REGION,
      updateEditableCountry: SettingsActions.UPDATE_EDITABLE_COUNTRY,
    }),
    countriesInRegion(region: Region): Country[] {
      const rid = region.id;
      return (this.editableCountries || []).filter(
        (c) => c.regionId != null && String(c.regionId) === String(rid),
      );
    },
    countryOptionsForRegion(region: Region): AutocompleteOption[] {
      const inRegion = new Set(this.countriesInRegion(region).map((c) => String(c.id)));
      return (this.editableCountries || [])
        .filter((c) => !c.isLocal && !inRegion.has(String(c.id)))
        .map((c) => ({ label: c.name, value: c.id }));
    },
    filteredCountryPickOptions(region: Region): AutocompleteOption[] {
      const q = this.countryPickSearch.trim().toLowerCase();
      const opts = this.countryOptionsForRegion(region);
      if (!q) {
        return opts;
      }
      return opts.filter((o) => o.label.toLowerCase().indexOf(q) > -1);
    },
    addCountryToRegion(region: Region, option: AutocompleteOption) {
      const regionKey = isRegionLocalId(region.id) ? region.id : Number(region.id);
      this.updateEditableCountry({
        id: option.value as Country['id'],
        regionId: regionKey as number | string,
      });
    },
    applyPickedCountries(region: Region) {
      const ids = [...this.pickedCountryIds];
      for (const id of ids) {
        this.addCountryToRegion(region, { value: id, label: '' });
      }
      this.pickedCountryIds = [];
      this.countryPickSearch = '';
    },
    removeCountryFromRegion(country: Country) {
      this.updateEditableCountry({
        id: country.id,
        regionId: null,
      });
    },
    toggleExpand(id: number | string) {
      if (this.expandedRegion === id) {
        this.expandedRegion = undefined;
      } else {
        this.expandedRegion = id;
      }
      this.pickedCountryIds = [];
      this.countryPickSearch = '';
    },
    addNewRegion() {
      const newRegion = new Region();
      this.expandedRegion = newRegion.id;
      this.pickedCountryIds = [];
      this.countryPickSearch = '';
      this.updateEditableList([...(this.editableRegions || []), newRegion]);
    },
    removeRegion(id: string | number) {
      const answer = window.confirm(this.$t('Do you really want to delete this Region?'));

      if (answer) {
        this.updateEditableList([...(this.editableRegions || []).filter((region) => region.id !== id)]);
      }
    },
  },
});
